import { useEffect, useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { createCampaign } from '@/lib/campaigns'
import { PageHeader } from '@/components/ui/page-header'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { ArrowLeft, Save, Link as LinkIcon, Layout, Zap, Sparkles } from 'lucide-react'
import { useBrands } from '@/lib/BrandsProvider'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { type CampaignType } from './campaign-type-selection'

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  notes: z.string().optional(),
  customIdea: z.string().optional(),
})

const campaignTypeInfo = {
  BASE: {
    label: 'Brand Campaign',
    icon: Layout,
    description: 'A campaign based on your brand strategy and positioning'
  },
  CUSTOM: {
    label: 'Custom Campaign',
    icon: Zap,
    description: 'A custom campaign tailored to your specific needs'
  },
  CONVERGENCE_BASED: {
    label: 'Convergence Campaign',
    icon: Sparkles,
    description: 'A campaign based on an AI-discovered convergence opportunity'
  }
} as const

export function NewCampaign() {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedBrand } = useBrands()
  const [loading, setLoading] = useState(false)
  const convergence = location.state?.convergence
  const campaignType = convergence ? 'CONVERGENCE_BASED' : (location.state?.campaignType || 'CUSTOM') as CampaignType

  const getDefaultCampaignName = () => {
    if (!selectedBrand) return ''

    if (convergence) {
      return `${convergence.trend} <> ${selectedBrand.brand_name}`
    }

    switch (campaignType) {
      case 'BASE':
        return `${selectedBrand.brand_name} brand campaign`
      case 'CUSTOM':
        return `${selectedBrand.brand_name} brilliant new campaign`
      default:
        return ''
    }
  }

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: getDefaultCampaignName(),
      notes: '',
      customIdea: '',
    },
  })

  useEffect(() => {
    if (!selectedBrand) {
      navigate('/brands')
    }
  }, [selectedBrand, navigate])

  useEffect(() => {
    if (selectedBrand) {
      form.setValue('name', getDefaultCampaignName())
    }
  }, [selectedBrand, campaignType, convergence])

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!selectedBrand) return

    setLoading(true)
    try {
      const { customIdea, ...campaignValues } = values
      
      const campaign = await createCampaign({
        ...campaignValues,
        brand_id: selectedBrand.id,
        originating_convergence_id: convergence?.id || null,
        campaign_type: campaignType,
        metadata: campaignType === 'CUSTOM' && customIdea ? {
          central_theme: customIdea
        } : undefined
      })
      navigate(`/campaigns/${campaign.id}`)
    } catch (error) {
      console.error('Error creating campaign:', error)
    } finally {
      setLoading(false)
    }
  }

  if (!selectedBrand) {
    return "No brand selected"
  }

  const TypeIcon = campaignTypeInfo[campaignType].icon

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      <div className="flex-none p-6 space-y-6">
        <div className="space-y-4">
          <PageHeader
            title="Create New Campaign"
            description="Create a new campaign for your brand"
            action={{
              label: 'Back',
              icon: ArrowLeft,
              onClick: () => navigate(-1),
            }}
          />
        </div>

        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-full bg-primary/10">
                  <TypeIcon className="h-5 w-5 text-primary" />
                </div>
                <Badge variant="secondary" className="text-sm">
                  {campaignTypeInfo[campaignType].label}
                </Badge>
              </div>
              {convergence && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <LinkIcon className="w-4 h-4" />
                  <Link
                    to={`/convergences/${convergence.id}`}
                    className="hover:underline text-primary"
                  >
                    View Source Convergence: {convergence.trend}
                  </Link>
                </div>
              )}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              {campaignTypeInfo[campaignType].description}
            </p>
            {convergence && (
              <Alert className="mt-4">
                <AlertDescription>
                  This campaign is based on the convergence "{convergence.trend}".
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>

      <ScrollArea className="flex-1 px-6 pb-6">
        <div className="bg-white rounded-lg border shadow-sm">
          <div className="p-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 max-w-2xl">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Campaign Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notes</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Add any tracking information around this campaign here"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {campaignType === 'CUSTOM' && (
                  <FormField
                    control={form.control}
                    name="customIdea"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Custom Campaign Idea</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="Describe the custom idea or theme you want to build your campaign around"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <Button type="submit" disabled={loading}>
                  {loading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Create Campaign
                    </>
                  )}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </ScrollArea>
    </div>
  )
}
